'use client'
import { ChainType } from '@polaris/core/models'
import { shorten } from '@polaris/core/utils'
import { ClientOnly } from '@quirks/react'
import { useAtomValue } from 'jotai'
import { WalletIcon } from '@/src/components/accounts/wallet-icon'
import {
  fromWalletAtom,
  isActiveAtom,
  isFirstStepErrorAtom,
  isFirstStepSuccessAtom,
  namadaUnshieldStepAtom,
  routeAtom,
  sourceChainAccountAtom,
  sourceChainAddressAtom,
  transferFundsToHotkeyStepAtom,
} from '@/src/stores/route-machine/atoms'
import { BasicStepRow, BasicStepRowTitle } from './basic-step-row'
import { PrivacyAccountLogo } from './privacy-account-logo'

const FromWalletIcon = () => {
  const route = useAtomValue(routeAtom)
  const fromWallet = useAtomValue(fromWalletAtom)
  const account = useAtomValue(sourceChainAccountAtom)

  // Check if source chain is Privacy Vault (Namada)
  const isPrivacyVault = route?.sourceChain.chain_type === ChainType.Namada

  if (isPrivacyVault) {
    return <PrivacyAccountLogo />
  }

  const imgUrl = account?.provider_info.img || fromWallet?.logoUrl
  const alt = account?.provider_info.name || fromWallet?.displayName

  if (!imgUrl || !alt) {
    return null
  }

  return (
    <ClientOnly>
      <WalletIcon
        logoUrl={imgUrl}
        walletName={alt}
        size={24}
        className='max-w-none'
      />
    </ClientOnly>
  )
}

const Title = () => {
  const route = useAtomValue(routeAtom)
  const fromWallet = useAtomValue(fromWalletAtom)
  const accountChainAddress = useAtomValue(sourceChainAddressAtom)

  const address = accountChainAddress || fromWallet?.address

  // Show "Privacy Vault" for Namada source instead of shortened address
  const isPrivacyVault = route?.sourceChain.chain_type === ChainType.Namada
  const displayName = isPrivacyVault
    ? 'Privacy Vault'
    : address
      ? shorten(address, { prefixLength: 8 })
      : ''

  return (
    <ClientOnly>
      <BasicStepRowTitle title={`From ${displayName}`} />
    </ClientOnly>
  )
}

export const FromWallet = () => {
  const isActive = useAtomValue(isActiveAtom)
  const fromWallet = useAtomValue(fromWalletAtom)
  const isFirstStepSuccess = useAtomValue(isFirstStepSuccessAtom)
  const isFirstStepError = useAtomValue(isFirstStepErrorAtom)
  const transferFundsToHotkeyStep = useAtomValue(transferFundsToHotkeyStepAtom)
  const namadaUnshieldStep = useAtomValue(namadaUnshieldStepAtom)
  const route = useAtomValue(routeAtom)

  // Funds historically left wallet if autopilot was funded or any step started at all
  const hasHistoricalFundsTransfer = route ? route.hasStarted : false

  // If this is historical route, we can return early and not require wallet info
  if (hasHistoricalFundsTransfer) {
    return (
      <BasicStepRow
        title={<Title />}
        icon={<FromWalletIcon />}
        isActive={false}
        isSuccess={true}
        isError={false}
        isLoading={false}
        isWaiting={false}
        iconOnly
      />
    )
  }

  if (!fromWallet || fromWallet.address === '' || !route?.sourceChain) {
    return null
  }

  // For privacy routes from Namada, use Namada unshield step status
  // For other MPC routes, use transfer to hotkey step status
  // Otherwise, use first step success
  const isLoading = namadaUnshieldStep.isNeeded
    ? namadaUnshieldStep.isLoading
    : transferFundsToHotkeyStep.isNeeded
      ? transferFundsToHotkeyStep.isLoading
      : undefined
  const isSuccess = namadaUnshieldStep.isNeeded
    ? namadaUnshieldStep.isSuccess
    : transferFundsToHotkeyStep.isNeeded
      ? transferFundsToHotkeyStep.isSuccess
      : isFirstStepSuccess

  // Namada unshield is automated and never requires user signing
  const isWaiting = namadaUnshieldStep.isNeeded
    ? false
    : transferFundsToHotkeyStep.isSigning

  return (
    <BasicStepRow
      title={<Title />}
      icon={<FromWalletIcon />}
      isActive={isActive}
      isSuccess={isSuccess || hasHistoricalFundsTransfer}
      isError={isFirstStepError}
      isLoading={isLoading}
      isWaiting={isWaiting}
      iconOnly
    />
  )
}
